import {
  O365LastCollection,
  O365VerifyStatus,
} from '../../../services/o365/o365Onboarding/o365Onboarding.model';

// List statuses (Connected / AwaitingConsent / PendingPermissions) and the verify-only ones
// (PendingConsent / Error).
export const statusDisplay: {
  [key: string]: { label: string; color: string; tooltip: string };
} = {
  AwaitingConsent: {
    label: 'Awaiting client consent',
    color: 'default',
    tooltip: "The client hasn't opened the link and granted consent yet.",
  },
  PendingConsent: {
    label: 'Waiting for consent',
    color: 'gold',
    tooltip:
      "Microsoft refused Synapse 360's app-only token: admin consent is missing, was revoked, or is still being applied.",
  },
  PendingPermissions: {
    label: 'Missing permissions',
    color: 'gold',
    tooltip:
      'A Microsoft 365 tenant is linked, but at least one Office 365 API was refused or not tested yet. Run Verify for details.',
  },
  Connected: {
    label: 'Connected',
    color: 'success',
    tooltip: 'Every Office 365 API answered; collection can run.',
  },
  Error: {
    label: 'Error',
    color: 'error',
    tooltip: 'Verification failed unexpectedly - see the server log.',
  },
  NotConfigured: {
    label: 'Not configured',
    color: 'default',
    tooltip:
      'No Office 365 APIs are enabled in the API group linked to the Microsoft Graph API type - check SPS > Config API and API Type.',
  },
};

export const getStatusDisplay = (status: string) =>
  statusDisplay[status] || { label: status, color: 'default', tooltip: '' };

// Verify-dialog-only explanations: the table tooltips above double as a status summary for every
// row at once, so they hedge ("also when..."). Inside the Verify dialog we already know the exact
// checks and the reason, so we can say what actually happened instead.
export const verifyDialogDescription: { [key in O365VerifyStatus]?: string } = {
  Connected:
    "Synapse 360 can read this tenant's Office 365 data. Rows marked Error or Not tested didn't block the connection.",
  PendingConsent: "Synapse 360 couldn't get access to this tenant yet - see the reason below.",
  PendingPermissions:
    'Some Office 365 APIs refused access - grant the missing permissions (see the hints) and verify again.',
  NotConfigured:
    'No Office 365 APIs are enabled in the API group linked to the Microsoft Graph API type - check SPS > Config API and API Type.',
};

export const outcomeDisplay: { [key: string]: { label: string; color: string } } = {
  Granted: { label: 'Granted', color: 'green' },
  Missing: { label: 'Missing', color: 'red' },
  NotTested: { label: 'Not tested', color: 'default' },
  Error: { label: 'Error', color: 'orange' },
  NotAvailable: { label: 'Not available', color: 'gold' },
};

export const getOutcomeDisplay = (outcome: string) =>
  outcomeDisplay[outcome] || { label: outcome, color: 'default' };

export const privacyDisplay: {
  [key: string]: { type: 'success' | 'warning' | 'info'; message: string };
} = {
  Visible: {
    type: 'success',
    message: 'Report names are visible: collected usage can be matched to users.',
  },
  Concealed: {
    type: 'warning',
    message: "Report names are concealed: usage is collected, but it can't be matched to users.",
  },
  NotDeterminable: {
    type: 'info',
    message: "Report privacy couldn't be determined: the Active User Detail report returned no rows.",
  },
  NotTested: {
    type: 'info',
    message: "Report privacy wasn't tested: the Active User Detail check didn't succeed.",
  },
};

export const lastCollectionText = (lastCollection: O365LastCollection | null): string => {
  if (!lastCollection) return 'Never';
  const { date, errors, succeeded, total, expected, stale } = lastCollection;
  const denominator = expected ?? total;
  let text =
    errors > 0
      ? `${date} · ${errors} ${errors === 1 ? 'error' : 'errors'}`
      : `${date} · ${succeeded} of ${denominator} succeeded`;
  if (expected !== undefined && stale) {
    text += ` · ${stale} not run`;
  }
  return text;
};

export const clientLink = (linkToken: string) =>
  `${window.location.origin}/connect-office365/${linkToken}`;
